import { valueAt } from '@n2-common/lib/utils';
import { TFunction } from 'i18next';
import { AmountInput, FormikInput, FormikSelect } from '../../ui';
import { OptionProps } from '../../ui/Select';
import { amountNotEmpty } from '../../validation/common/amount';
import { stringRequired } from '../../validation/common/string';
import { addPleaseSelectOption } from '../MultiStageOnePageWizard';
import { schemaOfType } from '@n2-common/lib/yup-helpers';
import type { StageField } from '../MultiStageOnePageWizard/types';
import { Question } from '@n2-common/serverGeneratedTypings/question';
import { AnswerOption } from '@n2-common/serverGeneratedTypings/answerOption';
import { ValidationError } from 'yup';
import { CompareValidation } from '@n2-common/applicationPages/CddPage/cddSpec';
import { PreFilledAnswer } from '.';
import * as Yup from 'yup';

type FormFromQuestions<Q extends Question[]> = { [K in Extract<Q[number]['QuestionCode'], string>]: string | null };

export function formikFieldsFromQuestions<Questions extends Question[], TForm extends FormFromQuestions<Questions>>(
    questions: Questions,
    t: TFunction,
    countries: AnswerOption[],
    preFilledAnswersToHide: PreFilledAnswer[] | null | undefined
): StageField<TForm>[] {
    return questions.map((q) => {
        let answerOptions: OptionProps[] = [];
        if (q.QuestionType.toLowerCase() === 'countrycode') {
            answerOptions = countries.map((o) => ({
                value: o.AnswerCode,
                text: o.TranslationText,
            }));
        } else if (q.AnswerOptions.length) {
            answerOptions = q.AnswerOptions.map((o) => ({
                value: o.AnswerCode,
                text: o.TranslationText,
            }));
        }
        const isSelect = answerOptions.length > 0;
        const preFilled = preFilledAnswersToHide ? preFilledAnswersToHide.find((m) => m.questionCode === q.QuestionCode) : null;
        const isNumeric = q.IsNumeric === true;
        const dependsOnFieldName = q.DependsOnQuestionCode;
        const fieldValidation = isNumeric ? amountNotEmpty(q.TranslationText, t) : q.IsOptional ? Yup.string().notRequired() : stringRequired(t);
        const field: StageField<TForm> = {
            name: q.QuestionCode as StageField<TForm>['name'],

            hidden: (values) => {
                if (!dependsOnFieldName) {
                    return preFilled ? !!preFilled.answerCode : false;
                }
                const dependentValue = valueAt(values, dependsOnFieldName as PathsOfType<TForm>);
                return !dependentValue || !q.ShowIfValues.includes(dependentValue as string);
            },
            validation: dependsOnFieldName
                ? schemaOfType(fieldValidation).test(
                      `is-dependent-on-${dependsOnFieldName}-check`,
                      t<string>('validation:string.required'),
                      async function test(val, ctx) {
                          const values = ctx.parent || ctx.options.context?.formikValues;

                          /*                           if (values && !values[dependsOnFieldName]) {
                              console.warn(`No reference to '${dependsOnFieldName}', when validating '${q.QuestionCode}'.`, ctx);
                          } */
                          if (values && dependsOnFieldName in values && q.ShowIfValues.includes(values[dependsOnFieldName])) {
                              try {
                                  // NOTE: scheme.validateSync is communicating errors via throwing ValidationError!
                                  await fieldValidation.validateSync(val);
                                  return true;
                              } catch (err) {
                                  if (err instanceof ValidationError) {
                                      return ctx.createError({ message: err.message });
                                  }
                                  return false;
                              }
                          }
                          return true;
                      }
                  )
                : fieldValidation,

            Component: (props) =>
                isSelect ? (
                    <FormikSelect options={addPleaseSelectOption(answerOptions, t)} {...props} />
                ) : isNumeric ? (
                    <AmountInput {...props} />
                ) : (
                    <FormikInput type={q.QuestionCode.toLowerCase().endsWith('date') ? 'date' : 'text'} {...props} />
                ),
            componentProps: { label: `${q.TranslationText}` },
        };
        const comparrison = CompareValidation.find((cv) => cv.questionCode == q.QuestionCode);
        if (comparrison) {
            field.validation = field.validation.test(`compare-to-${comparrison.compareTo}`, t<string>(comparrison.message), function test(val, ctx) {
                const dependsOnFieldName = comparrison.compareTo;
                const values = (dependsOnFieldName in ctx.parent && ctx.parent) || ctx.options.context?.value;

                /*                 if (values && !values[dependsOnFieldName]) {
                    console.warn(`No reference to '${dependsOnFieldName}', when validating '${q.QuestionCode}'.`, ctx);
                } */
                if (values && dependsOnFieldName in values) {
                    return comparrison.validate(val, values[dependsOnFieldName]);
                }
                return true;
            });
        }
        return field;
    });
}
